import {
  vertexShaderSource,
  fragmentShaderSource,
  gridVertexShaderSource,
  gridFragmentShaderSource,
  textVertexShaderSource,
  textFragmentShaderSource,
  sunCircleVertexShaderSource,
  sunCircleFragmentShaderSource
} from './shaders'
import { compileShader, createProgram } from './utils'

export class WebGLRenderer {
  private gl: WebGLRenderingContext
  private program: WebGLProgram | null = null
  private vertexBuffer: WebGLBuffer | null = null
  private animationTime: number = 0

  // Astronomical constants
  private readonly SUN_RADIUS_KM: number = 696000  // km
  private readonly SUN_EARTH_DISTANCE_KM: number = 149600000  // km (1 AU)
  private readonly SPEED_OF_LIGHT_KM_S: number = 299792.458  // km/s

  // Calculated astronomical properties
  private readonly lightTravelTimeSeconds: number
  private readonly sunAngularRadiusDegrees: number

  // Coordinate system properties
  private gridProgram: WebGLProgram | null = null
  private gridVertexBuffer: WebGLBuffer | null = null
  private verticalMin: number = 0  // degrees (will be set via updateViewingRange)
  private verticalMax: number = 10  // degrees (will be set via updateViewingRange)
  private horizontalMin: number = 0  // degrees (calculated based on aspect ratio)
  private horizontalMax: number = 0  // degrees (calculated based on aspect ratio)

  // Text label properties
  private textCanvas: HTMLCanvasElement
  private textContext: CanvasRenderingContext2D
  private textTexture: WebGLTexture | null = null
  private textProgram: WebGLProgram | null = null
  private textVertexBuffer: WebGLBuffer | null = null
  private textTexCoordBuffer: WebGLBuffer | null = null

  // Sun circle rendering
  private sunCircleProgram: WebGLProgram | null = null
  private sunCircleVertexBuffer: WebGLBuffer | null = null

  // Sun position
  private sunAngularPosition: number = 0  // degrees, 0 at horizon (6pm), +90 at noon, -90 at midnight

  constructor(canvas: HTMLCanvasElement) {
    const gl = canvas.getContext('webgl')
    if (!gl) {
      throw new Error('WebGL not supported')
    }
    this.gl = gl

    // Calculate astronomical properties
    // Light travel time from sun to earth
    this.lightTravelTimeSeconds = this.SUN_EARTH_DISTANCE_KM / this.SPEED_OF_LIGHT_KM_S

    // Angular size of the sun (using small angle approximation: angle = diameter / distance)
    // Convert to degrees: radians * (180 / PI)
    const sunAngularRadiusRadians = Math.atan(this.SUN_RADIUS_KM / this.SUN_EARTH_DISTANCE_KM)
    this.sunAngularRadiusDegrees = sunAngularRadiusRadians * (180 / Math.PI)

    // Create off-screen canvas for text rendering
    this.textCanvas = document.createElement('canvas')
    const ctx = this.textCanvas.getContext('2d')
    if (!ctx) {
      throw new Error('2D context not supported')
    }
    this.textContext = ctx

    this.initialize()
  }

  private initialize(): void {
    const gl = this.gl

    // Compile shaders for main scene
    const vertexShader = compileShader(gl, gl.VERTEX_SHADER, vertexShaderSource)
    const fragmentShader = compileShader(gl, gl.FRAGMENT_SHADER, fragmentShaderSource)

    // Create main program
    this.program = createProgram(gl, vertexShader, fragmentShader)

    // Compile shaders for grid
    const gridVertexShader = compileShader(gl, gl.VERTEX_SHADER, gridVertexShaderSource)
    const gridFragmentShader = compileShader(gl, gl.FRAGMENT_SHADER, gridFragmentShaderSource)

    // Create grid program
    this.gridProgram = createProgram(gl, gridVertexShader, gridFragmentShader)

    // Compile shaders for text labels
    const textVertexShader = compileShader(gl, gl.VERTEX_SHADER, textVertexShaderSource)
    const textFragmentShader = compileShader(gl, gl.FRAGMENT_SHADER, textFragmentShaderSource)

    // Create text program
    this.textProgram = createProgram(gl, textVertexShader, textFragmentShader)

    // Compile shaders for sun circles
    const sunCircleVertexShader = compileShader(gl, gl.VERTEX_SHADER, sunCircleVertexShaderSource)
    const sunCircleFragmentShader = compileShader(gl, gl.FRAGMENT_SHADER, sunCircleFragmentShaderSource)

    // Create sun circle program
    this.sunCircleProgram = createProgram(gl, sunCircleVertexShader, sunCircleFragmentShader)

    // Create a full-screen quad
    const vertices = new Float32Array([
      -1, -1,
       1, -1,
      -1,  1,
       1,  1,
    ])

    this.vertexBuffer = gl.createBuffer()
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vertexBuffer)
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW)

    // Create buffer for grid lines (will be populated dynamically)
    this.gridVertexBuffer = gl.createBuffer()

    // Create buffers for text labels (will be populated dynamically)
    this.textVertexBuffer = gl.createBuffer()
    this.textTexCoordBuffer = gl.createBuffer()

    // Create buffer for sun circles (will use same full-screen quad)
    this.sunCircleVertexBuffer = this.vertexBuffer

    // Create texture for text labels
    this.textTexture = gl.createTexture()

    // Set clear color
    gl.clearColor(0.0, 0.0, 0.0, 1.0)
  }


  // Helper methods to convert degree coordinates to NDC
  private degToNDCX(deg: number): number {
    return -1 + 2 * (deg - this.horizontalMin) / (this.horizontalMax - this.horizontalMin)
  }

  private degToNDCY(deg: number): number {
    return -1 + 2 * (deg - this.verticalMin) / (this.verticalMax - this.verticalMin)
  }

  private generateTickMarks(): Float32Array {
    const vertices: number[] = []
    const majorTickSize = 0.04  // Size of major tick marks in NDC (increased for visibility)
    const minorTickSize = 0.02  // Size of minor tick marks in NDC (increased for visibility)

    // Helper to convert degree position to NDC
    const degToNDCX = (deg: number): number => {
      return -1 + 2 * (deg - this.horizontalMin) / (this.horizontalMax - this.horizontalMin)
    }
    const degToNDCY = (deg: number): number => {
      return -1 + 2 * (deg - this.verticalMin) / (this.verticalMax - this.verticalMin)
    }

    // Determine tick spacing based on vertical viewing angle
    const verticalRange = this.verticalMax - this.verticalMin

    // Four zoom levels:
    // - Extreme zoom: vertical angle < 0.5° (range < 0.75°)
    // - Very zoomed in: vertical angle < 2° (range < 3°)
    // - Zoomed in: vertical angle < 5° (range < 7.5°)
    // - Normal: vertical angle >= 5°
    let majorTickInterval: number
    let minorTickInterval: number

    if (verticalRange < 0.75) {
      // Extreme zoom: angle < 0.5°
      majorTickInterval = 0.1
      minorTickInterval = 0.02
    } else if (verticalRange < 3.0) {
      // Very zoomed in: angle < 2°
      majorTickInterval = 0.4
      minorTickInterval = 0.1
    } else if (verticalRange < 7.5) {
      // Zoomed in: angle < 5°
      majorTickInterval = 1
      minorTickInterval = 0.2
    } else {
      // Normal zoom
      majorTickInterval = 5
      minorTickInterval = 1
    }

    // Vertical tick marks (left and right edges)
    // Generate minor ticks
    const verticalStart = Math.floor(this.verticalMin / minorTickInterval) * minorTickInterval
    const verticalEnd = Math.ceil(this.verticalMax / minorTickInterval) * minorTickInterval

    for (let deg = verticalStart; deg <= verticalEnd; deg += minorTickInterval) {
      // Skip if out of bounds
      if (deg < this.verticalMin || deg > this.verticalMax) continue

      // Round to avoid floating point issues (precision depends on zoom level)
      const roundedDeg = Math.round(deg * 100) / 100
      const isMajor = Math.abs(roundedDeg % majorTickInterval) < 0.001
      const tickSize = isMajor ? majorTickSize : minorTickSize
      const y = degToNDCY(roundedDeg)

      // Left edge tick
      vertices.push(-1, y, -1 + tickSize, y)

      // Right edge tick
      vertices.push(1, y, 1 - tickSize, y)
    }

    // Horizontal tick marks (top and bottom edges)
    const horizontalStart = Math.floor(this.horizontalMin / minorTickInterval) * minorTickInterval
    const horizontalEnd = Math.ceil(this.horizontalMax / minorTickInterval) * minorTickInterval

    for (let deg = horizontalStart; deg <= horizontalEnd; deg += minorTickInterval) {
      // Skip if out of bounds
      if (deg < this.horizontalMin || deg > this.horizontalMax) continue

      // Round to avoid floating point issues (precision depends on zoom level)
      const roundedDeg = Math.round(deg * 100) / 100
      const isMajor = Math.abs(roundedDeg % majorTickInterval) < 0.001
      const tickSize = isMajor ? majorTickSize : minorTickSize
      const x = degToNDCX(roundedDeg)

      // Bottom edge tick
      vertices.push(x, -1, x, -1 + tickSize)

      // Top edge tick
      vertices.push(x, 1, x, 1 - tickSize)
    }

    return new Float32Array(vertices)
  }

  private renderTextLabels(): { positions: Float32Array; texCoords: Float32Array; count: number } {
    const gl = this.gl
    const ctx = this.textContext
    const labels: Array<{ text: string; x: number; y: number }> = []

    // Get aspect ratio for proper text sizing
    const canvas = gl.canvas as HTMLCanvasElement
    const aspectRatio = canvas.width / canvas.height

    // Helper to convert degree position to NDC
    const degToNDCX = (deg: number): number => {
      return -1 + 2 * (deg - this.horizontalMin) / (this.horizontalMax - this.horizontalMin)
    }
    const degToNDCY = (deg: number): number => {
      return -1 + 2 * (deg - this.verticalMin) / (this.verticalMax - this.verticalMin)
    }

    // Determine tick spacing based on vertical viewing angle (must match generateTickMarks)
    const verticalRange = this.verticalMax - this.verticalMin

    let majorTickInterval: number
    let minorTickInterval: number

    if (verticalRange < 0.75) {
      // Extreme zoom: angle < 0.5°
      majorTickInterval = 0.1
      minorTickInterval = 0.02
    } else if (verticalRange < 3.0) {
      // Very zoomed in: angle < 2°
      majorTickInterval = 0.4
      minorTickInterval = 0.1
    } else if (verticalRange < 7.5) {
      // Zoomed in: angle < 5°
      majorTickInterval = 1
      minorTickInterval = 0.2
    } else {
      // Normal zoom
      majorTickInterval = 5
      minorTickInterval = 1
    }

    // Collect all major tick labels
    const verticalMin = Math.floor(this.verticalMin / minorTickInterval) * minorTickInterval
    const verticalMax = Math.ceil(this.verticalMax / minorTickInterval) * minorTickInterval
    const horizontalMin = Math.floor(this.horizontalMin / minorTickInterval) * minorTickInterval
    const horizontalMax = Math.ceil(this.horizontalMax / minorTickInterval) * minorTickInterval

    // Vertical labels - skip first and last
    for (let deg = verticalMin; deg <= verticalMax; deg += minorTickInterval) {
      if (deg < this.verticalMin || deg > this.verticalMax) continue
      const roundedDeg = Math.round(deg * 100) / 100
      const isMajor = Math.abs(roundedDeg % majorTickInterval) < 0.001
      if (isMajor && roundedDeg !== Math.round(this.verticalMin * 100) / 100 && roundedDeg !== Math.round(this.verticalMax * 100) / 100) {
        const y = degToNDCY(roundedDeg)
        // Left edge label
        labels.push({ text: `${roundedDeg}°`, x: -1 + 0.08, y })
        // Right edge label
        labels.push({ text: `${roundedDeg}°`, x: 1 - 0.08, y })
      }
    }

    // Horizontal labels - skip first and last
    for (let deg = horizontalMin; deg <= horizontalMax; deg += minorTickInterval) {
      if (deg < this.horizontalMin || deg > this.horizontalMax) continue
      const roundedDeg = Math.round(deg * 100) / 100
      const isMajor = Math.abs(roundedDeg % majorTickInterval) < 0.001
      if (isMajor && roundedDeg !== Math.round(this.horizontalMin * 100) / 100 && roundedDeg !== Math.round(this.horizontalMax * 100) / 100) {
        const x = degToNDCX(roundedDeg)
        // Bottom edge label
        labels.push({ text: `${roundedDeg}°`, x, y: -1 + 0.08 })
        // Top edge label
        labels.push({ text: `${roundedDeg}°`, x, y: 1 - 0.08 })
      }
    }

    if (labels.length === 0) {
      return { positions: new Float32Array(0), texCoords: new Float32Array(0), count: 0 }
    }

    // Use higher resolution for text rendering
    const textScale = 2 // Render at 2x resolution for sharper text

    // Calculate canvas size needed for all labels
    const labelWidth = 60 * textScale
    const labelHeight = 24 * textScale
    const labelsPerRow = Math.ceil(Math.sqrt(labels.length))
    const canvasWidth = labelWidth * labelsPerRow
    const canvasHeight = labelHeight * Math.ceil(labels.length / labelsPerRow)

    this.textCanvas.width = canvasWidth
    this.textCanvas.height = canvasHeight

    // Clear canvas
    ctx.clearRect(0, 0, canvasWidth, canvasHeight)

    // Set up text rendering with higher resolution
    ctx.font = `${16 * textScale}px sans-serif`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillStyle = 'white'

    // Enable better text rendering
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'

    // Render each label to the texture atlas
    const positions: number[] = []
    const texCoords: number[] = []

    labels.forEach((label, i) => {
      const col = i % labelsPerRow
      const row = Math.floor(i / labelsPerRow)
      const canvasX = col * labelWidth + labelWidth / 2
      const canvasY = row * labelHeight + labelHeight / 2

      // Draw text to canvas
      ctx.fillText(label.text, canvasX, canvasY)

      // Calculate texture coordinates
      const u0 = col * labelWidth / canvasWidth
      const v0 = row * labelHeight / canvasHeight
      const u1 = (col + 1) * labelWidth / canvasWidth
      const v1 = (row + 1) * labelHeight / canvasHeight

      // Calculate label size in NDC, accounting for aspect ratio
      // Base size in vertical NDC space
      const labelHeightNDC = 0.04
      // Width should maintain proper aspect ratio (labelWidth/labelHeight from texture)
      const labelAspectRatio = 60 / 24 // Original label dimensions
      const labelWidthNDC = labelHeightNDC * labelAspectRatio / aspectRatio

      // Create quad for this label
      const x = label.x
      const y = label.y
      const hw = labelWidthNDC / 2
      const hh = labelHeightNDC / 2

      // Two triangles for the quad
      positions.push(
        x - hw, y - hh,
        x + hw, y - hh,
        x - hw, y + hh,
        x + hw, y - hh,
        x + hw, y + hh,
        x - hw, y + hh
      )

      texCoords.push(
        u0, v1,
        u1, v1,
        u0, v0,
        u1, v1,
        u1, v0,
        u0, v0
      )
    })

    // Upload texture to GPU
    gl.bindTexture(gl.TEXTURE_2D, this.textTexture)
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, this.textCanvas)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)

    return {
      positions: new Float32Array(positions),
      texCoords: new Float32Array(texCoords),
      count: positions.length / 2
    }
  }

  public resize(width: number, height: number): void {
    const gl = this.gl
    const canvas = gl.canvas as HTMLCanvasElement

    // Set the actual resolution
    canvas.width = width
    canvas.height = height

    // Set the display size (CSS pixels)
    const dpr = window.devicePixelRatio || 1
    canvas.style.width = `${width / dpr}px`
    canvas.style.height = `${height / dpr}px`

    gl.viewport(0, 0, width, height)

    // Calculate horizontal degree range based on aspect ratio
    const aspectRatio = width / height
    const verticalRange = this.verticalMax - this.verticalMin
    const horizontalRange = verticalRange * aspectRatio
    const horizontalCenter = 0
    this.horizontalMin = horizontalCenter - horizontalRange / 2
    this.horizontalMax = horizontalCenter + horizontalRange / 2
  }

  public updateViewingRange(verticalViewingAngle: number): void {
    // Calculate vertical range with 2:1 ratio (above:below horizon)
    // Horizon is at 0°, above is positive, below is negative
    const aboveHorizon = verticalViewingAngle
    const belowHorizon = verticalViewingAngle / 2

    this.verticalMax = aboveHorizon   // e.g., 10° above for angle=10
    this.verticalMin = -belowHorizon  // e.g., -5° below for angle=10

    // Recalculate horizontal range to maintain square grid cells
    const canvas = this.gl.canvas as HTMLCanvasElement
    const aspectRatio = canvas.width / canvas.height
    const verticalRange = this.verticalMax - this.verticalMin
    const horizontalRange = verticalRange * aspectRatio
    const horizontalCenter = 0
    this.horizontalMin = horizontalCenter - horizontalRange / 2
    this.horizontalMax = horizontalCenter + horizontalRange / 2
  }

  public updateTime(time: Date): void {
    // Calculate sun angular position based on time of day
    // 0° at 6pm (18:00), +90° at 6am (sunrise), -90° at 6pm+12h (next day)
    // The sun moves 15° per hour (360° / 24 hours)
    // Moving forward in time from 6pm should move the sun DOWN (negative)
    const hours = time.getHours()
    const minutes = time.getMinutes()
    const seconds = time.getSeconds()
    const milliseconds = time.getMilliseconds()

    // Convert to decimal hours
    const decimalHours = hours + minutes / 60 + seconds / 3600 + milliseconds / 3600000

    // Calculate angular position
    // At 18:00 (6pm), position should be 0°
    // At 06:00 (6am), position should be +90° (sun rising in the morning)
    // At 00:00 (midnight), position should be -90° (sun below horizon at night)
    // Formula: angle = (18 - hours) * 15
    // This makes the sun move DOWN as time progresses past 6pm
    this.sunAngularPosition = (18 - decimalHours) * 15
  }

  public render(): void {
    const gl = this.gl

    if (!this.program) return

    this.animationTime += 0.01

    const width = gl.canvas.width
    const height = gl.canvas.height

    gl.clear(gl.COLOR_BUFFER_BIT)

    // === PASS 1: Render main scene ===
    gl.useProgram(this.program)

    // Setup vertex attributes for main program
    const positionLocation = gl.getAttribLocation(this.program, 'a_position')
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vertexBuffer)
    gl.enableVertexAttribArray(positionLocation)
    gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0)

    // Calculate sun position and radius for shader
    const sunImageX = this.degToNDCX(0)
    const sunImageY = this.degToNDCY(this.sunAngularPosition)
    const verticalRange = this.verticalMax - this.verticalMin
    const radiusNDC = (this.sunAngularRadiusDegrees / verticalRange) * 2

    // Update uniforms
    const timeLocation = gl.getUniformLocation(this.program, 'u_time')
    const resolutionLocation = gl.getUniformLocation(this.program, 'u_resolution')
    const sunCenterLocation = gl.getUniformLocation(this.program, 'u_sunCenter')
    const sunRadiusLocation = gl.getUniformLocation(this.program, 'u_sunRadius')
    gl.uniform1f(timeLocation, this.animationTime)
    gl.uniform2f(resolutionLocation, width, height)
    gl.uniform2f(sunCenterLocation, sunImageX, sunImageY)
    gl.uniform1f(sunRadiusLocation, radiusNDC)

    // Draw scene
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)

    // Disable attributes from previous program
    gl.disableVertexAttribArray(positionLocation)

    // === PASS 2: Draw coordinate grid tick marks ===
    if (this.gridProgram) {
      gl.useProgram(this.gridProgram)

      // Generate tick mark geometry
      const tickVertices = this.generateTickMarks()

      // Upload to GPU
      gl.bindBuffer(gl.ARRAY_BUFFER, this.gridVertexBuffer)
      gl.bufferData(gl.ARRAY_BUFFER, tickVertices, gl.DYNAMIC_DRAW)

      // Setup vertex attributes
      const gridPositionLocation = gl.getAttribLocation(this.gridProgram, 'a_position')
      gl.enableVertexAttribArray(gridPositionLocation)
      gl.vertexAttribPointer(gridPositionLocation, 2, gl.FLOAT, false, 0, 0)

      // Draw tick marks as lines
      gl.drawArrays(gl.LINES, 0, tickVertices.length / 2)

      // Disable grid attributes
      gl.disableVertexAttribArray(gridPositionLocation)
    }

    // === PASS 3: Draw text labels ===
    if (this.textProgram) {
      gl.useProgram(this.textProgram)

      // Generate text label geometry and texture
      const textData = this.renderTextLabels()

      if (textData.count > 0) {
        // Enable blending for text rendering
        gl.enable(gl.BLEND)
        gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA)

        // Upload position data
        gl.bindBuffer(gl.ARRAY_BUFFER, this.textVertexBuffer)
        gl.bufferData(gl.ARRAY_BUFFER, textData.positions, gl.DYNAMIC_DRAW)

        const textPositionLocation = gl.getAttribLocation(this.textProgram, 'a_position')
        gl.enableVertexAttribArray(textPositionLocation)
        gl.vertexAttribPointer(textPositionLocation, 2, gl.FLOAT, false, 0, 0)

        // Upload texture coordinate data
        gl.bindBuffer(gl.ARRAY_BUFFER, this.textTexCoordBuffer)
        gl.bufferData(gl.ARRAY_BUFFER, textData.texCoords, gl.DYNAMIC_DRAW)

        const textTexCoordLocation = gl.getAttribLocation(this.textProgram, 'a_texCoord')
        gl.enableVertexAttribArray(textTexCoordLocation)
        gl.vertexAttribPointer(textTexCoordLocation, 2, gl.FLOAT, false, 0, 0)

        // Bind text texture
        gl.activeTexture(gl.TEXTURE0)
        gl.bindTexture(gl.TEXTURE_2D, this.textTexture)

        const textTextureLocation = gl.getUniformLocation(this.textProgram, 'u_texture')
        gl.uniform1i(textTextureLocation, 0)

        // Draw text labels
        gl.drawArrays(gl.TRIANGLES, 0, textData.count)

        // Disable text attributes
        gl.disableVertexAttribArray(textPositionLocation)
        gl.disableVertexAttribArray(textTexCoordLocation)

        // Disable blending
        gl.disable(gl.BLEND)
      }
    }

    // === PASS 4: Draw sun position circles ===
    if (this.sunCircleProgram) {
      gl.useProgram(this.sunCircleProgram)

      // Enable blending for smooth circle edges
      gl.enable(gl.BLEND)
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA)

      // Setup vertex attributes
      const sunCirclePositionLocation = gl.getAttribLocation(this.sunCircleProgram, 'a_position')
      gl.bindBuffer(gl.ARRAY_BUFFER, this.sunCircleVertexBuffer)
      gl.enableVertexAttribArray(sunCirclePositionLocation)
      gl.vertexAttribPointer(sunCirclePositionLocation, 2, gl.FLOAT, false, 0, 0)

      // Get uniform locations
      const centerLocation = gl.getUniformLocation(this.sunCircleProgram, 'u_center')
      const radiusLocation = gl.getUniformLocation(this.sunCircleProgram, 'u_radius')
      const lineWidthLocation = gl.getUniformLocation(this.sunCircleProgram, 'u_lineWidth')
      const dashTypeLocation = gl.getUniformLocation(this.sunCircleProgram, 'u_dashType')
      const resolutionLocation = gl.getUniformLocation(this.sunCircleProgram, 'u_resolution')

      gl.uniform2f(resolutionLocation, width, height)

      // Sun's image position (where we see it)
      // Horizontal position is always 0° (directly in front of observer)
      // Vertical position is based on time of day (sunAngularPosition)
      const sunImageX = this.degToNDCX(0)
      const sunImageY = this.degToNDCY(this.sunAngularPosition)

      // Calculate the radius in NDC based on angular size
      // We need to convert angular degrees to NDC space
      const verticalRange = this.verticalMax - this.verticalMin
      const radiusNDC = (this.sunAngularRadiusDegrees / verticalRange) * 2

      // Draw dashed circle for sun's image position
      gl.uniform2f(centerLocation, sunImageX, sunImageY)
      gl.uniform1f(radiusLocation, radiusNDC)
      gl.uniform1f(lineWidthLocation, 0.005) // Thin line
      gl.uniform1i(dashTypeLocation, 0) // 0 = dashed
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)

      // Calculate sun's true position (accounting for light travel time)
      // The sun moves at 15 degrees per hour = 0.25 degrees per minute
      const sunAngularVelocityDegPerMin = 0.25
      const lightTravelTimeMinutes = this.lightTravelTimeSeconds / 60
      const sunTruePositionOffsetDeg = sunAngularVelocityDegPerMin * lightTravelTimeMinutes

      // True position is offset from image position by light travel time
      const sunTrueY = this.degToNDCY(this.sunAngularPosition - sunTruePositionOffsetDeg)

      // Draw dotted circle for sun's true position
      gl.uniform2f(centerLocation, sunImageX, sunTrueY)
      gl.uniform1f(radiusLocation, radiusNDC)
      gl.uniform1f(lineWidthLocation, 0.005) // Thin line
      gl.uniform1i(dashTypeLocation, 1) // 1 = dotted
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)

      // Disable attributes
      gl.disableVertexAttribArray(sunCirclePositionLocation)

      // Disable blending
      gl.disable(gl.BLEND)
    }
  }

  public dispose(): void {
    const gl = this.gl

    if (this.vertexBuffer) {
      gl.deleteBuffer(this.vertexBuffer)
    }
    if (this.gridVertexBuffer) {
      gl.deleteBuffer(this.gridVertexBuffer)
    }
    if (this.textVertexBuffer) {
      gl.deleteBuffer(this.textVertexBuffer)
    }
    if (this.textTexCoordBuffer) {
      gl.deleteBuffer(this.textTexCoordBuffer)
    }
    if (this.program) {
      gl.deleteProgram(this.program)
    }
    if (this.gridProgram) {
      gl.deleteProgram(this.gridProgram)
    }
    if (this.textProgram) {
      gl.deleteProgram(this.textProgram)
    }
    if (this.sunCircleProgram) {
      gl.deleteProgram(this.sunCircleProgram)
    }
    if (this.textTexture) {
      gl.deleteTexture(this.textTexture)
    }
  }
}
